import type { Arrow, Direction, GridPoint, Level } from './types';
import { calcMove, directionDelta, expandArrow } from './physics';

// ── Кривая сложности — крутить гейм-фил тут ──────────────────────────────────
const PLATEAU_LEVEL    = 20;               // с этого уровня параметры фиксированы
const GRID_START       = { cols: 6,  rows: 8  };
const GRID_END         = { cols: 20, rows: 26 };
const LEN_START        = { min: 3, max: 12  }; // длина змейки, клеток
const LEN_END          = { min: 5, max: 24 };
const DEPTH_BIAS_START = 0.15;             // вес глубоких вставок (цепочки зависимостей)
const DEPTH_BIAS_END   = 0.9;
const COVERAGE_START   = 0.85;             // целевая доля занятых клеток
const COVERAGE_END     = 0.92;
const SEED_MULT        = 1000003;          // разводит соседние уровни по пространству сидов
const N_SAMPLES        = 4;                // проб на вставку (rejection sampling с lookahead)
const STRAND_PENALTY   = 4;                // штраф за клетку, навсегда отрезанную от края

export interface Difficulty {
  cols: number; rows: number;
  minLen: number; maxLen: number;
  depthBias: number;
  targetCoverage: number;
}

export function difficultyFor(levelNum: number): Difficulty {
  const t = Math.min(1, Math.max(0, (levelNum - 1) / (PLATEAU_LEVEL - 1)));
  const lerp  = (a: number, b: number) => a + (b - a) * t;
  const lerpI = (a: number, b: number) => Math.round(lerp(a, b));
  return {
    cols: lerpI(GRID_START.cols, GRID_END.cols),
    rows: lerpI(GRID_START.rows, GRID_END.rows),
    minLen: lerpI(LEN_START.min, LEN_END.min),
    maxLen: lerpI(LEN_START.max, LEN_END.max),
    depthBias: lerp(DEPTH_BIAS_START, DEPTH_BIAS_END),
    targetCoverage: lerp(COVERAGE_START, COVERAGE_END),
  };
}

// ── Seeded RNG (паттерн zen-match/levels.ts) ─────────────────────────────────
export function parkMiller(seed: number): () => number {
  let s = seed | 0 || 1;
  return () => {
    s = ((Math.imul(s, 16807) % 2147483647) + 2147483647) % 2147483647 || 1;
    return (s - 1) / 2147483646;
  };
}

// ── Обратная вставка ─────────────────────────────────────────────────────────
// Играем задом наперёд: стрелка, вставленная ПОЗЖЕ, в реальной игре выходит
// РАНЬШЕ. Инвариант решаемости: луч выхода вставляемой стрелки свободен на
// момент вставки — позже вставленные стрелки могут его перекрыть (они выйдут
// раньше), поэтому луч occupied не помечается.

const DIRS: Direction[] = ['up', 'down', 'left', 'right'];

// Занятость поля — плоская Uint8Array (1 = занято), индекс r*cols+c. Строковых
// ключей нет нигде в горячем пути: генерация зовётся каждый кадр на старом Android.

interface Candidate {
  col: number; row: number; // клетка головы
  dir: Direction;           // направление выхода
  depth: number;            // клеток между головой и краем по лучу
}

/**
 * Все пары (клетка, направление) с полностью свободным лучом выхода.
 * Для каждого направления идём по линии от края выхода вглубь: свободные
 * клетки до первой занятой имеют чистый луч по построению обхода.
 */
function collectCandidates(cols: number, rows: number, grid: Uint8Array): Candidate[] {
  const out: Candidate[] = [];
  for (let r = 0; r < rows; r++) {
    const base = r * cols;
    for (let c = cols - 1, depth = 0; c >= 0 && !grid[base + c]; c--, depth++)
      out.push({ col: c, row: r, dir: 'right', depth });
    for (let c = 0, depth = 0; c < cols && !grid[base + c]; c++, depth++)
      out.push({ col: c, row: r, dir: 'left', depth });
  }
  for (let c = 0; c < cols; c++) {
    for (let r = rows - 1, depth = 0; r >= 0 && !grid[r * cols + c]; r--, depth++)
      out.push({ col: c, row: r, dir: 'down', depth });
    for (let r = 0, depth = 0; r < rows && !grid[r * cols + c]; r++, depth++)
      out.push({ col: c, row: r, dir: 'up', depth });
  }
  return out;
}

/** Клетка позади головы (против направления выхода) свободна и в поле — нужна для длины ≥ 2. */
function behindFree(cand: Candidate, cols: number, rows: number, grid: Uint8Array): boolean {
  const { dc, dr } = directionDelta(cand.dir);
  const c = cand.col - dc, r = cand.row - dr;
  return c >= 0 && c < cols && r >= 0 && r < rows && !grid[r * cols + c];
}

/** Взвешенный выбор: глубокие вставки ложатся на чужие лучи → цепочки «сначала убери ту». */
function pickCandidate(cands: Candidate[], depthBias: number, rng: () => number): Candidate {
  let sum = 0;
  const weights = cands.map(c => { const w = 1 + depthBias * c.depth; sum += w; return w; });
  let x = rng() * sum;
  for (let i = 0; i < cands.length; i++) {
    x -= weights[i];
    if (x <= 0) return cands[i];
  }
  return cands[cands.length - 1];
}

function freeNeighborCount(
  col: number, row: number, cols: number, rows: number,
  grid: Uint8Array, used: Uint8Array
): number {
  let n = 0;
  for (const d of DIRS) {
    const { dc, dr } = directionDelta(d);
    const c = col + dc, r = row + dr;
    if (c >= 0 && c < cols && r >= 0 && r < rows) {
      const i = r * cols + c;
      if (!grid[i] && !used[i]) n++;
    }
  }
  return n;
}

/** Сколько из 4 соседей — стена (за краем поля) или занятая клетка. */
function wallContact(
  col: number, row: number, cols: number, rows: number,
  grid: Uint8Array, used: Uint8Array
): number {
  let n = 0;
  for (const d of DIRS) {
    const { dc, dr } = directionDelta(d);
    const c = col + dc, r = row + dr;
    if (c < 0 || c >= cols || r < 0 || r >= rows) { n++; continue; }
    const i = r * cols + c;
    if (grid[i] && !used[i]) n++;
  }
  return n;
}

// Лучевые проверки — на плоской Uint8Array-сетке (1 = занято), индекс r*cols+c.
// Без строковых ключей: это горячий путь (reachInfo зовётся на каждой вставке).

/** Чистый горизонтальный луч до края (влево ИЛИ вправо). */
function clearH(col: number, row: number, cols: number, grid: Uint8Array): boolean {
  const base = row * cols;
  let ok = true;
  for (let c = col - 1; c >= 0; c--) if (grid[base + c]) { ok = false; break; }
  if (ok) return true;
  for (let c = col + 1; c < cols; c++) if (grid[base + c]) return false;
  return true;
}

/** Чистый вертикальный луч до края (вверх ИЛИ вниз). */
function clearV(col: number, row: number, rows: number, cols: number, grid: Uint8Array): boolean {
  let ok = true;
  for (let r = row - 1; r >= 0; r--) if (grid[r * cols + col]) { ok = false; break; }
  if (ok) return true;
  for (let r = row + 1; r < rows; r++) if (grid[r * cols + col]) return false;
  return true;
}

interface ReachInfo { H: Uint8Array; V: Uint8Array; } // клетки с чистым H- / V-лучом (1)

/**
 * Для всех свободных клеток — есть ли чистый горизонтальный (H) и/или вертикальный
 * (V) луч к краю. Клетка «заполнима» ⇔ H[i]|V[i]. Разбиение по осям нужно, чтобы
 * при пробной вставке пересчитывать только затронутую ось (см. countNewlyStranded).
 */
function reachInfo(cols: number, rows: number, grid: Uint8Array): ReachInfo {
  const n = cols * rows;
  const H = new Uint8Array(n), V = new Uint8Array(n);
  for (let r = 0; r < rows; r++)
    for (let c = 0; c < cols; c++) {
      const i = r * cols + c;
      if (grid[i]) continue;
      if (clearH(c, r, cols, grid)) H[i] = 1;
      if (clearV(c, r, rows, cols, grid)) V[i] = 1;
    }
  return { H, V };
}

/**
 * Сколько ранее-заполнимых свободных клеток пробная вставка тела `body`
 * отрежет от края навсегда. Меняется ray-статус только у клеток в строках и
 * столбцах тела (grid дополняется лишь там); при этом клетка теряет заполнимость
 * лишь если исчезает её ЕДИНСТВЕННАЯ ось выхода — поэтому клетки с сохранившимся
 * лучом по нетронутой оси пропускаются без пересчёта. `grid` временно
 * мутируется и восстанавливается.
 */
function countNewlyStranded(
  body: GridPoint[], cols: number, rows: number,
  grid: Uint8Array, reach: ReachInfo,
  rowT: Uint8Array, colT: Uint8Array, seen: Uint8Array
): number {
  // scratch-массивы (rowT/colT/seen) переданы из generateLevel и здесь же чистятся.
  const touched: number[] = []; // индексы, которые пометили в seen — чтобы сбросить
  for (const p of body) {
    const i = p.row * cols + p.col;
    grid[i] = 1; rowT[p.row] = 1; colT[p.col] = 1;
    seen[i] = 1; touched.push(i); // тело помечено «уже видели» → test его пропустит
  }

  let stranded = 0;
  const test = (c: number, r: number): void => {
    const i = r * cols + c;
    if (seen[i]) return;
    if (!reach.H[i] && !reach.V[i]) { seen[i] = 1; touched.push(i); return; } // не была заполнима
    seen[i] = 1; touched.push(i);
    let reachable: boolean;
    if (colT[c] && rowT[r]) reachable = clearH(c, r, cols, grid) || clearV(c, r, rows, cols, grid);
    else if (rowT[r])       reachable = reach.V[i] === 1 || clearH(c, r, cols, grid);       // V-ось не тронута
    else                    reachable = reach.H[i] === 1 || clearV(c, r, rows, cols, grid); // H-ось не тронута
    if (!reachable) stranded++;
  };
  for (const p of body) {
    for (let c = 0; c < cols; c++) test(c, p.row);
    for (let r = 0; r < rows; r++) test(p.col, r);
  }

  for (const i of touched) seen[i] = 0;
  for (const p of body) { grid[p.row * cols + p.col] = 0; rowT[p.row] = 0; colT[p.col] = 0; }
  return stranded;
}

/**
 * Растит хвост от головы случайным блужданием по свободным клеткам.
 * Первый шаг — строго назад от направления выхода (сегмент головы обязан
 * лежать вдоль direction, как во всех авторских уровнях). Дальше предпочитаем
 * соседей с минимумом свободных соседей — тупики заполняются первыми, дыр
 * остаётся меньше. Вернувшиеся клетки — в порядке head → tail, длина ≥ 2
 * (кандидат прошёл behindFree), но может быть короче targetLen — это и есть
 * «добор» коротких змеек в карманах.
 */
function growTail(
  cand: Candidate, targetLen: number, cols: number, rows: number,
  grid: Uint8Array, used: Uint8Array, rng: () => number
): GridPoint[] {
  const cells: GridPoint[] = [{ col: cand.col, row: cand.row }];
  used[cand.row * cols + cand.col] = 1;
  const { dc, dr } = directionDelta(cand.dir);

  // Запрет прямого луча выхода головы: тело не должно ложиться на клетки,
  // сквозь которые голова пройдёт при выходе — иначе стрелка визуально
  // проходит сквозь собственный хвост. Луч (head→край) гарантированно свободен
  // (инвариант collectCandidates), помечаем в scratch `used` и чистим в конце.
  const ray: number[] = [];
  for (let c = cand.col + dc, r = cand.row + dr; c >= 0 && c < cols && r >= 0 && r < rows; c += dc, r += dr) {
    const i = r * cols + c;
    if (grid[i]) break;
    used[i] = 1; ray.push(i);
  }

  let cur: GridPoint = { col: cand.col - dc, row: cand.row - dr };
  cells.push(cur);
  used[cur.row * cols + cur.col] = 1;

  while (cells.length < targetLen) {
    const opts: GridPoint[] = [];
    for (const d of DIRS) {
      const dd = directionDelta(d);
      const c = cur.col + dd.dc, r = cur.row + dd.dr;
      if (c < 0 || c >= cols || r < 0 || r >= rows) continue;
      const i = r * cols + c;
      if (grid[i] || used[i]) continue;
      opts.push({ col: c, row: r });
    }
    if (opts.length === 0) break;

    // Первично — минимум свободных соседей (тупики вперёд); вторично — максимум
    // контакта со стеной/занятыми (жмёмся к границе, свободное держим связным).
    let best: GridPoint[] = [];
    let bestScore = Infinity;
    let bestWall = -1;
    for (const o of opts) {
      const s = freeNeighborCount(o.col, o.row, cols, rows, grid, used);
      const w = wallContact(o.col, o.row, cols, rows, grid, used);
      if (s < bestScore || (s === bestScore && w > bestWall)) { bestScore = s; bestWall = w; best = [o]; }
      else if (s === bestScore && w === bestWall) best.push(o);
    }
    cur = best[Math.floor(rng() * best.length)];
    cells.push(cur);
    used[cur.row * cols + cur.col] = 1;
  }
  for (const cell of cells) used[cell.row * cols + cell.col] = 0; // очистить scratch
  for (const i of ray) used[i] = 0;                               // очистить луч
  return cells;
}

/** head→tail клетки → corner points формата Arrow.points (tail → head). */
function toPoints(cellsHeadToTail: GridPoint[]): GridPoint[] {
  const cells = cellsHeadToTail.slice().reverse();
  const pts: GridPoint[] = [{ ...cells[0] }];
  for (let i = 1; i < cells.length - 1; i++) {
    const a = cells[i - 1], b = cells[i], c = cells[i + 1];
    if ((c.col - b.col) !== (b.col - a.col) || (c.row - b.row) !== (b.row - a.row)) {
      pts.push({ ...b });
    }
  }
  pts.push({ ...cells[cells.length - 1] });
  return pts;
}

export function generateLevel(levelNum: number): Level {
  const d = difficultyFor(levelNum);
  const rng = parkMiller(levelNum * SEED_MULT + 1);
  const totalCells = d.cols * d.rows;
  const grid = new Uint8Array(totalCells);  // занятость поля
  const used = new Uint8Array(totalCells);  // scratch для growTail
  const seen = new Uint8Array(totalCells);  // scratch для countNewlyStranded
  const rowT = new Uint8Array(d.rows);      // scratch: тронутые строки тела
  const colT = new Uint8Array(d.cols);      // scratch: тронутые столбцы тела
  const arrows: Arrow[] = [];
  let filled = 0;

  while (filled / totalCells < d.targetCoverage) {
    const cands = collectCandidates(d.cols, d.rows, grid)
      .filter(c => behindFree(c, d.cols, d.rows, grid));
    if (cands.length === 0) break; // остались только 1-клеточные/недостижимые карманы

    // Rejection sampling с lookahead: пробуем N кандидатов, берём вставку с
    // максимальным score = заполнено − STRAND_PENALTY·(отрезанные навсегда клетки).
    // Кандидаты уже валидны (чистый луч + behindFree) → решаемость сохраняется,
    // выбор лишь избегает замуровывания больших областей.
    const reach = reachInfo(d.cols, d.rows, grid);
    let bestCells: GridPoint[] | null = null;
    let bestDir: Direction = cands[0].dir;
    let bestScore = -Infinity;
    for (let s = 0; s < N_SAMPLES; s++) {
      const cand = pickCandidate(cands, d.depthBias, rng);
      const targetLen = d.minLen + Math.floor(rng() * (d.maxLen - d.minLen + 1));
      const cells = growTail(cand, targetLen, d.cols, d.rows, grid, used, rng);
      const stranded = countNewlyStranded(cells, d.cols, d.rows, grid, reach, rowT, colT, seen);
      const score = cells.length - STRAND_PENALTY * stranded;
      if (score > bestScore) { bestScore = score; bestCells = cells; bestDir = cand.dir; }
    }

    for (const cell of bestCells!) grid[cell.row * d.cols + cell.col] = 1;
    filled += bestCells!.length;
    arrows.push({ id: `a${arrows.length + 1}`, direction: bestDir, points: toPoints(bestCells!) });
  }

  return { cols: d.cols, rows: d.rows, arrows };
}

// ── Верификация (dev-хук ?verify=N; в обычной игре не вызывается) ─────────────
export interface VerifyReport {
  level: number;
  arrows: number;
  coverage: number; // доля занятых клеток
  solved: boolean;
  depth: number;    // раундов волнового удаления (-1 = не решается)
  ms: number;       // время generateLevel
}

/**
 * Жадный солвер точен: уход стрелки только освобождает клетки (монотонность),
 * поэтому убирать можно волнами — за раунд всё, что может выйти. Число раундов
 * = глубина зависимостей; если раунд пуст, а стрелки остались — уровень не решается.
 */
function greedyDepth(level: Level): number {
  let arrows = level.arrows;
  let depth = 0;
  while (arrows.length > 0) {
    const exitable = arrows.filter(a =>
      calcMove(a, arrows.filter(o => o.id !== a.id), level).canExit);
    if (exitable.length === 0) return -1;
    const gone = new Set(exitable.map(a => a.id));
    arrows = arrows.filter(a => !gone.has(a.id));
    depth++;
  }
  return depth;
}

export function verifyLevels(count: number): VerifyReport[] {
  const out: VerifyReport[] = [];
  for (let n = 1; n <= count; n++) {
    const t0 = performance.now();
    const level = generateLevel(n);
    const ms = performance.now() - t0;
    const cells = level.arrows.reduce((s, a) => s + expandArrow(a).length, 0);
    const depth = greedyDepth(level);
    out.push({
      level: n,
      arrows: level.arrows.length,
      coverage: cells / (level.cols * level.rows),
      solved: depth > 0,
      depth,
      ms,
    });
  }
  return out;
}
